// ActivityTypeCard — activity-type selector card for ActivityWizard Steg 1 (delspår 3b).
// Renders the DB-stored emoji icon (activity_types.icon) directly per locked beslut #3
// (no lucide mapping). Name resolves name_en for en-UI (beslut #4). Player-count
// derives from min_participants/default_max_participants. The "?"-info icon renders
// only when the type has a description (beslut #5 — conditional, currently NULL for all).

import { cn } from "@/lib/utils";
import { Info, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ActivityType } from "@/hooks/useActivityTypes";

interface ActivityTypeCardProps {
  type: ActivityType;
  selected: boolean;
  onSelect: (id: string) => void;
  onInfoClick?: (type: ActivityType) => void;
}

export default function ActivityTypeCard({
  type,
  selected,
  onSelect,
  onInfoClick,
}: ActivityTypeCardProps) {
  const { t, i18n } = useTranslation("admin");
  const isEn = i18n.language?.startsWith("en");
  const name = (isEn ? type.name_en : null) ?? type.name;
  const description = (isEn ? type.description_en : null) ?? type.description;
  const hasDescription = !!description;

  const min = type.min_participants;
  const max = type.default_max_participants;
  let playerLabel: string | null = null;
  if (min != null && max != null) {
    playerLabel =
      min === max
        ? t("activityWizard.step1.playersExact", { count: min, defaultValue: `${min} spelare` })
        : t("activityWizard.step1.playersRange", { min, max, defaultValue: `${min}–${max} spelare` });
  } else if (min != null) {
    playerLabel = t("activityWizard.step1.playersExact", { count: min, defaultValue: `${min} spelare` });
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      data-testid={`activity-type-card-${type.slug}`}
      onClick={() => onSelect(type.id)}
      className={cn(
        "relative flex min-h-[112px] flex-col items-center justify-center gap-1.5 rounded-[22px] border p-3 text-center transition-all md:min-h-[140px] md:gap-2 md:p-4",
        selected
          ? "livfull-card-selected"
          : "border-border hover:border-primary/50 hover:shadow active:scale-[0.98]",
      )}
    >
      {hasDescription && onInfoClick && (
        <span
          role="button"
          tabIndex={0}
          aria-label={t("activityWizard.step1.infoTitle", { name, defaultValue: `Om ${name}` })}
          data-testid={`activity-type-info-${type.slug}`}
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick(type);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onInfoClick(type);
            }
          }}
          className={cn(
            "absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100",
            // Livfull: when selected the corner shows a check-badge instead — hide the info "?"
            // via className only (the stopPropagation handler stays attached — TRAP A / L76).
            selected && "hidden",
          )}
        >
          <Info className="h-4 w-4" />
        </span>
      )}
      {selected && (
        <span
          aria-hidden
          className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-primary"
        >
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      <span
        aria-hidden
        className={cn(
          "inline-flex h-12 w-12 items-center justify-center rounded-2xl text-3xl leading-none transition-colors md:h-14 md:w-14 md:text-4xl",
          selected ? "bg-white/[0.22]" : "bg-primary/10",
        )}
      >
        {type.icon}
      </span>
      <span className="text-sm font-semibold leading-tight">{name}</span>
      {playerLabel && (
        <span className={cn("text-xs", selected ? "text-primary-foreground/80" : "text-muted-foreground")}>
          {playerLabel}
        </span>
      )}
    </button>
  );
}
